import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import { PageHero, CTABand } from "@/components/PageHero";
import { Section, SectionHeading, InfoBox } from "@/components/Primitives";
import LeadForm from "@/components/LeadForm";

const IMG = "https://images.unsplash.com/photo-1769144256181-698b8f807066?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const SERVICES = [
  "International company formation", "Market-entry strategy", "Branch and subsidiary planning",
  "Business acquisition search", "Preliminary due-diligence coordination", "Import and export development",
  "Distributor search", "Sales-channel development", "Franchise expansion",
  "Virtual office and local representation", "Accounting, legal and banking referrals",
];

const GOALS = [
  { id: "open", label: "Open a foreign company", services: ["International company formation", "Branch and subsidiary planning", "Accounting, legal and banking referrals"] },
  { id: "purchase", label: "Purchase a business", services: ["Business acquisition search", "Preliminary due-diligence coordination", "Market-entry strategy"] },
  { id: "expand", label: "Expand my existing company", services: ["Market-entry strategy", "Branch and subsidiary planning", "Sales-channel development"] },
  { id: "distributors", label: "Find distributors", services: ["Distributor search", "Import and export development", "Sales-channel development"] },
  { id: "invest", label: "Explore an investment pathway", services: ["Preliminary due-diligence coordination", "Market-entry strategy", "Licensed professional referral"] },
  { id: "move", label: "Move with my business", services: ["International company formation", "Business acquisition search", "Licensed immigration professional referral"] },
];

export default function GlobalExpansion() {
  const [goal, setGoal] = useState(GOALS[0]);
  return (
    <>
      <SEO title="Global Business Expansion" description="International company formation, business purchase, market-entry and expansion advisory from Malaysia. Contact us to know more." path="/global-business-expansion" />
      <PageHero eyebrow="Global Business Expansion" image={IMG}
        title="Take Your Business Beyond Borders"
        subtitle="We help established business owners assess international markets, identify suitable structures and coordinate local support. Contact us to know more." />

      <Section className="bg-white">
        <SectionHeading eyebrow="Our Services" title="End-to-End Expansion Support" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {SERVICES.map((s) => (
            <div key={s} className="rounded-xl border border-slate-100 bg-slate-50/60 p-5 flex items-start gap-3 hover-lift">
              <span className="w-6 h-6 rounded-full bg-emerald-brand/10 flex items-center justify-center shrink-0 mt-0.5"><Check size={13} className="text-emerald-brand" /></span>
              <span className="text-sm font-medium text-navy">{s}</span>
            </div>
          ))}
        </div>
      </Section>

      <Section className="bg-slate-50">
        <SectionHeading eyebrow="Interactive Selector" title="What do you want to achieve?" center />
        <div className="grid lg:grid-cols-2 gap-8 items-start">
          <div className="grid sm:grid-cols-2 gap-3">
            {GOALS.map((g) => (
              <button key={g.id} data-testid={`goal-${g.id}`} onClick={() => setGoal(g)}
                className={`text-left rounded-xl border-2 px-5 py-4 font-semibold text-sm transition-all ${goal.id === g.id ? "border-emerald-brand bg-white text-navy shadow-md" : "border-slate-200 bg-white/60 text-slate-600 hover:border-slate-300"}`}>
                {g.label}
              </button>
            ))}
          </div>
          <div className="rounded-2xl bg-white border border-slate-100 shadow-lg p-6">
            <h3 className="font-bold text-navy">Recommended for: <span className="text-emerald-brand">{goal.label}</span></h3>
            <ul className="mt-4 space-y-2">
              {goal.services.map((s) => (
                <li key={s} className="flex items-center gap-2 text-sm text-navy"><ArrowRight size={14} className="text-gold" /> {s}</li>
              ))}
            </ul>
            <div className="mt-5"><InfoBox>The appropriate structure depends on your business and circumstances. Contact us to know more.</InfoBox></div>
            <div className="mt-5">
              <LeadForm formName={`Global Expansion — ${goal.label}`} serviceKey="global-expansion" testid="expansion-form" title="Request Tailored Support" />
            </div>
          </div>
        </div>
      </Section>

      <CTABand service="International expansion" serviceKey="global-expansion" />
    </>
  );
}
